'use client';

import { useTranslations } from 'next-intl';
import { LegalPageLayout } from '@/components/shared/legal-page-layout';

export default function TermsOfUse() {
  const t = useTranslations('pages.terms');
  const tableOfContents = t.raw('toc') as Array<{ id: string; title: string; level: number }>;
  const registrationItems = t.raw('sections.accounts.registration.items') as string[];
  const ageVerificationItems = t.raw('sections.accounts.ageVerification.items') as Array<{
    label: string;
    body: string;
  }>;
  const responsibilitiesItems = t.raw('sections.accounts.responsibilities.items') as string[];
  const terminationItems = t.raw('sections.accounts.termination.items') as string[];
  const prohibitedItems = t.raw('sections.userConduct.prohibited.items') as string[];
  const dataProcessingItems = t.raw('sections.dataProtection.processing.items') as string[];
  const gdprItems = t.raw('sections.dataProtection.gdpr.items') as Array<{
    label: string;
    body: string;
  }>;
  const euRightsItems = t.raw('sections.euConsumerRights.items') as Array<{
    label: string;
    body: string;
  }>;
  return (
    <LegalPageLayout
      description={t('description')}
      lastUpdated={t('lastUpdated')}
      tableOfContents={tableOfContents}
      title={t('title')}
    >
      <div className="space-y-8">
        <section id="introduction">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.introduction.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.introduction.body')}</p>
        </section>

        <section id="acceptance-of-terms">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.acceptance.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.acceptance.body1')}</p>
          <p className="mb-4 text-purple-200/80">{t('sections.acceptance.body2')}</p>
        </section>

        <section id="description-of-service">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.description.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.description.body')}</p>
        </section>

        <section id="user-accounts">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.accounts.title')}
          </h2>

          <div className="mb-6" id="account-registration">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.accounts.registration.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.accounts.registration.intro')}</p>
            <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
              {registrationItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="mb-4 text-purple-200/80">{t('sections.accounts.registration.note')}</p>
          </div>

          <div className="mb-6" id="account-responsibilities">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.accounts.responsibilities.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">
              {t('sections.accounts.responsibilities.intro')}
            </p>
            <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
              {responsibilitiesItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="mb-6" id="age-verification">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.accounts.ageVerification.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">
              {t('sections.accounts.ageVerification.intro')}
            </p>
            <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
              {ageVerificationItems.map((item) => (
                <li key={item.label}>
                  <strong>{item.label}</strong> {item.body}
                </li>
              ))}
            </ul>
            <p className="mb-4 text-purple-200/80">{t('sections.accounts.ageVerification.note')}</p>
          </div>

          <div className="mb-6" id="account-termination">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.accounts.termination.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.accounts.termination.intro')}</p>
            <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
              {terminationItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="mb-4 text-purple-200/80">{t('sections.accounts.termination.note')}</p>
          </div>
        </section>

        <section id="user-conduct">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.userConduct.title')}
          </h2>

          <div className="mb-6" id="prohibited-activities">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.userConduct.prohibited.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.userConduct.prohibited.intro')}</p>
            <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
              {prohibitedItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="mb-6" id="content-standards">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.userConduct.content.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.userConduct.content.body')}</p>
          </div>
        </section>

        <section id="intellectual-property">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.intellectualProperty.title')}
          </h2>

          <div className="mb-6" id="service-content">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.intellectualProperty.serviceContent.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">
              {t('sections.intellectualProperty.serviceContent.body')}
            </p>
          </div>

          <div className="mb-6" id="user-generated-content">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.intellectualProperty.userGenerated.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">
              {t('sections.intellectualProperty.userGenerated.body')}
            </p>
          </div>

          <div className="mb-6" id="trademarks">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.intellectualProperty.trademarks.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">
              {t('sections.intellectualProperty.trademarks.body')}
            </p>
          </div>
        </section>

        <section id="paid-services">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.paid.title')}
          </h2>

          <div className="mb-6" id="subscription-terms">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.paid.subscription.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.paid.subscription.body')}</p>
          </div>

          <div className="mb-6" id="payment-processing">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.paid.payment.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.paid.payment.body')}</p>
          </div>

          <div className="mb-6" id="refunds">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.paid.refunds.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.paid.refunds.body')}</p>
          </div>
        </section>

        <section id="disclaimers">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.disclaimers.title')}
          </h2>

          <div className="mb-6" id="astrological-disclaimer">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.disclaimers.astrology.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.disclaimers.astrology.body')}</p>
          </div>

          <div className="mb-6" id="service-availability">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.disclaimers.availability.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.disclaimers.availability.body')}</p>
          </div>
        </section>

        <section id="data-protection-and-consent">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.dataProtection.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.dataProtection.intro')}</p>

          <div className="mb-6" id="gdpr-consent">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.dataProtection.gdpr.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">{t('sections.dataProtection.gdpr.intro')}</p>
            <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
              {gdprItems.map((item) => (
                <li key={item.label}>
                  <strong>{item.label}</strong> {item.body}
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-6" id="data-processing-agreement">
            <h3 className="mb-3 font-body font-semibold text-purple-100 text-xl">
              {t('sections.dataProtection.processing.title')}
            </h3>
            <p className="mb-4 text-purple-200/80">
              {t('sections.dataProtection.processing.intro')}
            </p>
            <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
              {dataProcessingItems.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </section>

        <section id="limitation-of-liability">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.liability.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.liability.body1')}</p>
          <p className="mb-4 text-purple-200/80">{t('sections.liability.body2')}</p>
        </section>

        <section id="indemnification">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.indemnification.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.indemnification.body')}</p>
        </section>

        <section id="privacy">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.privacy.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.privacy.body')}</p>
        </section>

        <section id="termination">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.termination.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.termination.body')}</p>
        </section>

        <section id="governing-law">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.governingLaw.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.governingLaw.body')}</p>
        </section>

        <section id="eu-consumer-rights">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.euConsumerRights.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.euConsumerRights.intro')}</p>
          <ul className="mb-4 ml-6 list-disc space-y-2 text-purple-200/80">
            {euRightsItems.map((item) => (
              <li key={item.label}>
                <strong>{item.label}</strong> {item.body}
              </li>
            ))}
          </ul>
          <p className="mb-4 text-purple-200/80">{t('sections.euConsumerRights.note')}</p>
        </section>

        <section id="changes-to-terms">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.changes.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.changes.body')}</p>
        </section>

        <section id="contact-information">
          <h2 className="mb-4 font-display font-semibold text-2xl text-white">
            {t('sections.contact.title')}
          </h2>
          <p className="mb-4 text-purple-200/80">{t('sections.contact.intro')}</p>
          <div className="mb-4 text-purple-200/80">
            <p>{t('sections.contact.email')}</p>
          </div>
        </section>
      </div>
    </LegalPageLayout>
  );
}
